// UltraFlex - Day Pass Success Page (shows QR after payment)
import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import QRCode from 'react-qr-code';
import { Check, Calendar, MapPin, AlertCircle, Loader, Dumbbell, Copy, CheckCheck } from 'lucide-react';
import { DayPassWalletButtons } from '../components/WalletButtons';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DayPassSuccess = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [dayPass, setDayPass] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [attempts, setAttempts] = useState(0);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (sessionId) pollStatus();
  }, [sessionId]);

  const pollStatus = async (attempt = 0) => {
    if (attempt >= 10) {
      setError('Payment verification timed out. Please check your email for confirmation or contact reception.');
      setLoading(false);
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/day-pass/status/${sessionId}`);
      if (!res.ok) {
        if (attempt < 3) {
          setTimeout(() => pollStatus(attempt + 1), 3000);
          return;
        }
        setError('Unable to verify payment. Please contact reception with your confirmation email.');
        setLoading(false);
        return;
      }
      const data = await res.json();
      if (data.payment_status === 'paid') {
        setDayPass(data);
        setLoading(false);
        // Store pass link in localStorage so guest can retrieve it later
        if (data.id) {
          const viewUrl = `${window.location.origin}/day-pass/view/${data.id}`;
          localStorage.setItem('uf_last_daypass_url', viewUrl);
          localStorage.setItem('uf_last_daypass_id', data.id);
        }
        return;
      }
      if (data.payment_status === 'expired' || data.payment_status === 'failed') {
        setError('Payment was not completed. Please try again.');
        setLoading(false);
        return;
      }
      // Keep polling (payment still processing)
      setAttempts(attempt + 1);
      setTimeout(() => pollStatus(attempt + 1), 2500);
    } catch (e) {
      if (attempt < 3) {
        setTimeout(() => pollStatus(attempt + 1), 3000);
        return;
      }
      setError('Unable to verify payment. Please contact reception if you have been charged.');
      setLoading(false);
    }
  };

  if (!sessionId) {
    return (
      <div className="min-h-screen bg-[#0a0a1a] flex items-center justify-center p-4">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-gray-500 mx-auto mb-4" />
          <p className="text-gray-400">Invalid session. Please purchase a day pass first.</p>
          <Link to="/day-pass" className="inline-block mt-4 bg-[#e63946] text-white px-6 py-2 rounded-lg text-sm">Buy Day Pass</Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a1a] flex items-center justify-center p-4" data-testid="loading-payment">
        <div className="text-center">
          <Loader className="w-12 h-12 text-[#e63946] mx-auto mb-4 animate-spin" />
          <p className="text-white text-lg font-semibold">Confirming your payment...</p>
          <p className="text-gray-400 text-sm mt-2">This may take a moment</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0a0a1a] flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <p className="text-red-400 text-lg">{error}</p>
          <Link to="/day-pass" className="inline-block mt-6 bg-[#e63946] text-white px-6 py-2 rounded-lg text-sm">Try Again</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a1a] flex items-center justify-center p-4" data-testid="day-pass-success">
      <div className="max-w-md w-full">
        {/* Success Header */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Check className="w-8 h-8 text-green-400" />
          </div>
          <h1 className="text-2xl font-black text-white">You're all set!</h1>
          <p className="text-gray-400 mt-1">Your day pass has been confirmed</p>
        </div>

        {/* Day Pass Card */}
        <div className="bg-gradient-to-br from-[#1a1a2e] to-[#16213e] rounded-2xl border border-gray-700 overflow-hidden">
          {/* Top ribbon */}
          <div className="bg-[#e63946] px-6 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Dumbbell className="w-5 h-5 text-white" />
              <span className="text-white font-bold text-sm">ULTRAFLEX DAY PASS</span>
            </div>
            <span className="text-white/80 text-xs">#{dayPass?.id?.slice(0, 8)}</span>
          </div>

          {/* Details */}
          <div className="p-6 space-y-4">
            <div className="flex items-center gap-3">
              <MapPin className="w-5 h-5 text-[#e63946]" />
              <div>
                <p className="text-xs text-gray-400">Gym Location</p>
                <p className="text-white font-semibold">{dayPass?.site_name}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Calendar className="w-5 h-5 text-[#e63946]" />
              <div>
                <p className="text-xs text-gray-400">Valid On</p>
                <p className="text-white font-semibold">
                  {dayPass?.visit_date && new Date(dayPass.visit_date + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                </p>
              </div>
            </div>
            <div className="text-center text-gray-400 text-xs">{dayPass?.first_name} {dayPass?.last_name}</div>

            {/* QR Code */}
            {dayPass?.qr_token && (
              <div className="bg-white rounded-xl p-6 mx-auto max-w-[220px]" data-testid="day-pass-qr">
                <QRCode value={dayPass.qr_token} size={180} level="H" />
              </div>
            )}
            <p className="text-center text-gray-400 text-xs">Show this QR code at the gym entrance</p>
          </div>
        </div>

        {/* Info */}
        <div className="mt-6 bg-[#1a1a2e] rounded-xl border border-gray-700 p-4 space-y-3">
          <p className="text-gray-400 text-xs leading-relaxed">
            A confirmation email will be sent to <span className="text-white">{dayPass?.email}</span>.
            This pass is valid for one day only at the selected location.
          </p>
          {dayPass?.id && (
            <div className="flex gap-2">
              <Link
                to={`/day-pass/view/${dayPass.id}`}
                className="flex-1 bg-[#e63946] text-white text-center px-4 py-2.5 rounded-lg text-sm font-semibold hover:bg-red-500 transition-colors"
                data-testid="view-pass-link"
              >
                Open My Pass
              </Link>
              <button
                onClick={() => {
                  const url = `${window.location.origin}/day-pass/view/${dayPass.id}`;
                  navigator.clipboard.writeText(url).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
                }}
                className="flex items-center gap-1.5 bg-gray-700 text-white px-4 py-2.5 rounded-lg text-sm hover:bg-gray-600 transition-colors"
                data-testid="copy-pass-link"
              >
                {copied ? <CheckCheck className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                {copied ? 'Copied!' : 'Copy Link'}
              </button>
            </div>
          )}
        </div>

        {/* Wallet Pass Buttons */}
        {dayPass?.id && (
          <div className="mt-4 bg-[#1a1a2e] rounded-xl border border-gray-700 p-4" data-testid="daypass-success-wallet">
            <DayPassWalletButtons retrievalCode={dayPass.id} />
          </div>
        )}

        <div className="mt-6 text-center space-y-3">
          <Link to="/" className="block text-gray-400 text-sm hover:text-white">Back to UltraFlex</Link>
          <Link to="/join" className="block text-[#e63946] text-sm font-semibold hover:text-red-400">Interested in a full membership? Join now &rarr;</Link>
        </div>
      </div>
    </div>
  );
};

export default DayPassSuccess;
